import {
  faChevronDown,
  faChevronUp,
  faCommentAlt,
  faHistory,
  faInfoCircle,
  faPencilAlt,
  faPlusCircle,
  faSpinner,
  faSyncAlt,
  faTrash,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useContext, useState } from "react";
import Button, { EButtonColor } from "../../../components/UI/Button/Button";
import ConfirmDialog from "../../../components/UI/ConfirmDialog/ConfirmDialog";
import ModalContext, { defaultModal } from "../../../components/UI/Modal/ModalContext";
import Spinner from "../../../components/UI/Spinner/Spinner";
import { IService } from "../../../interfaces";
import LeadService, {
  IAuditTrail,
  ILeadActivityItem,
  ILeadNote,
} from "../../../services/LeadService";
import classes from "./OrganizationNotes.module.scss";

interface IProps {
  organizationId: string;
  services?: IService[];
}

type TimelineFilter = "ALL" | "NOTES" | "AUDIT";

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("fi-FI", {
      day: "numeric",
      month: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch (e) {
    return dateStr;
  }
};

const timeAgo = (dateStr?: string) => {
  if (!dateStr) return "";
  const now = new Date();
  const past = new Date(dateStr);
  const diffMs = now.getTime() - past.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffDay > 30) {
    return past.toLocaleDateString("fi-FI");
  } else if (diffDay > 0) {
    return `${diffDay} pv sitten`;
  } else if (diffHour > 0) {
    return `${diffHour} t sitten`;
  } else if (diffMin > 0) {
    return `${diffMin} min sitten`;
  } else {
    return "juuri nyt";
  }
};

const getInitials = (name?: string) => {
  if (!name) return "HX";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const OrganizationNotes: React.FC<IProps> = ({ organizationId, services }) => {
  const modal = useContext(ModalContext);

  const [isOpen, setIsOpen] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState<ILeadNote[]>([]);
  const [auditTrails, setAuditTrails] = useState<IAuditTrail[]>([]);
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>("ALL");

  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [selectedServiceToken, setSelectedServiceToken] = useState<string>("");

  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState("");
  const [savingEditNote, setSavingEditNote] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [loadedNotes, loadedAudits] = await Promise.all([
        LeadService.listOrganizationNotes(organizationId),
        LeadService.listAuditTrailsForTarget(organizationId),
      ]);
      setNotes(loadedNotes || []);
      setAuditTrails(loadedAudits || []);
      setHasLoaded(true);
    } catch (err) {
      console.error("Failed to load organization activities:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (!isOpen && !hasLoaded) {
      loadData();
    }
    setIsOpen(!isOpen);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || savingNote) return;

    try {
      setSavingNote(true);
      let serviceName: string | undefined = undefined;
      if (selectedServiceToken && services) {
        const found = services.find((s) => s.serviceToken === selectedServiceToken);
        if (found) serviceName = found.name;
      }

      const saved = await LeadService.addOrganizationNote(
        organizationId,
        newNote.trim(),
        selectedServiceToken || undefined,
        serviceName
      );

      setNotes((prev) => [saved, ...prev]);
      setNewNote("");
    } catch (err) {
      console.error("Failed to add note:", err);
    } finally {
      setSavingNote(false);
    }
  };

  const handleStartEditNote = (note: ILeadNote) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingNoteContent("");
  };

  const handleSaveEditNote = async (noteId: string) => {
    if (!editingNoteContent.trim() || savingEditNote) return;

    try {
      setSavingEditNote(true);
      const updated = await LeadService.updateLeadNote(noteId, editingNoteContent.trim());
      setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
      setEditingNoteId(null);
      setEditingNoteContent("");
    } catch (err) {
      console.error("Failed to update note:", err);
    } finally {
      setSavingEditNote(false);
    }
  };

  const handleDeleteNote = (noteId: string) => {
    modal.setModal({
      ...defaultModal,
      isOpen: true,
      title: "Poista muistiinpano",
      content: (
        <ConfirmDialog
          body="Haluatko varmasti poistaa tämän muistiinpanon?"
          onConfirm={async () => {
            modal.setModal(defaultModal);
            try {
              await LeadService.deleteLeadNote(noteId);
              setNotes((prev) => prev.filter((n) => n.id !== noteId));
            } catch (err) {
              console.error("Failed to delete note:", err);
            }
          }}
          onCancel={() => modal.setModal(defaultModal)}
        />
      ),
    });
  };

  const timelineItems: ILeadActivityItem[] = LeadService.buildOrganizationTimeline({
    notes,
    auditTrails,
  });

  const filteredTimelineItems = timelineItems.filter((item) => {
    if (timelineFilter === "NOTES") return item.type === "NOTE";
    if (timelineFilter === "AUDIT") return item.type !== "NOTE";
    return true;
  });

  const availableServices = (services || []).filter((s) => s.serviceToken);
  const noteCount = notes.length;
  const auditCount = timelineItems.filter((i) => i.type !== "NOTE").length;
  const totalCount = timelineItems.length;

  return (
    <div className={classes.OrganizationNotesWrapper}>
      <div className={classes.ToggleBtnWrapper}>
        <Button
          onClick={handleToggle}
          color={EButtonColor.NONE}
          style={{
            backgroundColor: isOpen ? "#e2e8f0" : "#f8fafc",
            color: "#1e293b",
            border: "1px solid #cbd5e1",
            fontWeight: 600,
            borderRadius: "6px",
            padding: "0.55rem 1.2rem",
          }}
        >
          <FontAwesomeIcon icon={faHistory} style={{ marginRight: "0.5rem" }} />
          {loading ? (
            <>
              Ladataan aktiviteetteja... <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: "0.5rem" }} />
            </>
          ) : isOpen ? (
            <>
              Sulje aktiviteetit ja muutoshistoria ({hasLoaded ? totalCount : "..."}){" "}
              <FontAwesomeIcon icon={faChevronUp} style={{ marginLeft: "0.5rem" }} />
            </>
          ) : (
            <>
              {hasLoaded ? `Aktiviteetit ja muutoshistoria (${totalCount})` : "Avaa aktiviteetit ja muutoshistoria"}{" "}
              <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: "0.5rem" }} />
            </>
          )}
        </Button>
      </div>

      {isOpen && (
        <div className={classes.SectionBox}>
          <h4>
            <span>Aktiviteetit ja muutoshistoria {hasLoaded && `(${totalCount})`}</span>
          </h4>

          {/* New note input */}
          <div className={classes.InputWrapper}>
            {availableServices.length > 1 && (
              <div className={classes.ServiceSelectRow}>
                <span>Liitä palveluun:</span>
                <select
                  value={selectedServiceToken}
                  onChange={(e) => setSelectedServiceToken(e.target.value)}
                  disabled={savingNote}
                >
                  <option value="">-- Yleinen (kaikki palvelut) --</option>
                  {availableServices.map((svc) => (
                    <option key={svc.id || svc.serviceToken} value={svc.serviceToken}>
                      {svc.name || svc.serviceToken}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={classes.InputRow}>
              <textarea
                placeholder="Kirjoita uusi muistiinpano... (Enter tallentaa, Shift+Enter rivinvaihto)"
                value={newNote}
                rows={2}
                onChange={(e) => setNewNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleAddNote();
                  }
                }}
                disabled={savingNote}
              />
              <Button
                onClick={handleAddNote}
                disabled={savingNote || !newNote.trim()}
                style={{ height: "42px", padding: "0 1.25rem", whiteSpace: "nowrap" }}
              >
                {savingNote ? <FontAwesomeIcon icon={faSpinner} spin /> : "Lisää muistiinpano"}
              </Button>
            </div>
          </div>

          {/* Filter tabs */}
          <div className={classes.TimelineFilters}>
            <button
              className={`${classes.FilterPill} ${timelineFilter === "ALL" ? classes.FilterPillActive : ""}`}
              onClick={() => setTimelineFilter("ALL")}
            >
              Kaikki ({totalCount})
            </button>
            <button
              className={`${classes.FilterPill} ${timelineFilter === "NOTES" ? classes.FilterPillActive : ""}`}
              onClick={() => setTimelineFilter("NOTES")}
            >
              Muistiinpanot ({noteCount})
            </button>
            <button
              className={`${classes.FilterPill} ${timelineFilter === "AUDIT" ? classes.FilterPillActive : ""}`}
              onClick={() => setTimelineFilter("AUDIT")}
            >
              Muutoshistoria & Lokit ({auditCount})
            </button>
          </div>

          {/* Timeline list */}
          <div className={classes.Timeline}>
            {loading ? (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <Spinner />
              </div>
            ) : filteredTimelineItems.length === 0 ? (
              <div className={classes.EmptyTimeline}>
                Ei näytettäviä tapahtumia valitulla suodatuksella.
              </div>
            ) : (
              filteredTimelineItems.map((item) => (
                <div className={classes.TimelineItem} key={item.id}>
                  {/* Left Icon Badge */}
                  <div
                    className={`${classes.TimelineIconTrack} ${
                      item.type === "NOTE"
                        ? classes.TimelineIconNote
                        : item.type === "CONVERTED"
                        ? classes.TimelineIconConvert
                        : item.type === "CREATED"
                        ? classes.TimelineIconCreate
                        : classes.TimelineIconStatus
                    }`}
                  >
                    {item.type === "NOTE" && <FontAwesomeIcon icon={faCommentAlt} />}
                    {item.type === "UPDATE" && <FontAwesomeIcon icon={faSyncAlt} />}
                    {item.type === "CONVERTED" && <FontAwesomeIcon icon={faUserPlus} />}
                    {item.type === "CREATED" && <FontAwesomeIcon icon={faPlusCircle} />}
                    {item.type === "SYSTEM" && <FontAwesomeIcon icon={faInfoCircle} />}
                  </div>

                  {/* Event Card */}
                  <div className={classes.TimelineCard}>
                    <div className={classes.TimelineHeader}>
                      <div className={classes.TimelineAuthorMeta}>
                        {item.type === "NOTE" && (
                          <div className={classes.AvatarCircle}>
                            {getInitials(item.authorName)}
                          </div>
                        )}
                        <span className={classes.TimelineAuthorName}>{item.authorName || item.title}</span>
                        <span>·</span>
                        <span className={classes.TimelineDate} title={formatDateTime(item.timestamp)}>
                          {timeAgo(item.timestamp)}
                        </span>
                        {item.serviceName && (
                          <span className={classes.ServiceBadge}>{item.serviceName}</span>
                        )}
                      </div>

                      {item.type === "NOTE" && item.noteId && (
                        <div className={classes.TimelineActions}>
                          <button
                            className={classes.ItemEditBtn}
                            title="Muokkaa muistiinpanoa"
                            onClick={() => handleStartEditNote(item.rawNote!)}
                          >
                            <FontAwesomeIcon icon={faPencilAlt} />
                          </button>
                          <button
                            className={classes.ItemDeleteBtn}
                            title="Poista muistiinpano"
                            onClick={() => handleDeleteNote(item.noteId!)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Note editing mode */}
                    {item.type === "NOTE" && editingNoteId === item.noteId ? (
                      <div className={classes.EditNoteBox}>
                        <textarea
                          value={editingNoteContent}
                          onChange={(e) => setEditingNoteContent(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              if (editingNoteContent.trim() && !savingEditNote) {
                                handleSaveEditNote(item.noteId!);
                              }
                            }
                          }}
                          rows={2}
                          disabled={savingEditNote}
                        />
                        <div className={classes.EditActions}>
                          <button
                            onClick={handleCancelEditNote}
                            className={classes.CancelBtn}
                          >
                            Peruuta
                          </button>
                          <Button
                            onClick={() => handleSaveEditNote(item.noteId!)}
                            disabled={savingEditNote || !editingNoteContent.trim()}
                            style={{ padding: "0.2rem 0.6rem", fontSize: "0.78rem" }}
                          >
                            {savingEditNote ? <FontAwesomeIcon icon={faSpinner} spin /> : "Tallenna"}
                          </Button>
                        </div>
                      </div>
                    ) : item.type === "UPDATE" && item.oldValue !== undefined && item.newValue !== undefined ? (
                      <div className={classes.ChangeDiffBox}>
                        <span className={classes.FieldLabel}>{item.title}</span>
                        <div className={classes.DiffRow}>
                          <span className={classes.DiffOld}>{item.oldValue || "(tyhjä)"}</span>
                          <span className={classes.DiffArrow}>➔</span>
                          <span className={classes.DiffNew}>{item.newValue || "(tyhjä)"}</span>
                        </div>
                      </div>
                    ) : (
                      <div className={classes.TimelineContent}>
                        {item.description || item.title}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationNotes;

